import { Divider, Grid, IconButton, List, ListItem, ListItemText, OutlinedInput } from '@mui/material'
import { DeckAccordion } from 'pages/deck/common/DeckAccordion'
import AddIcon from '@mui/icons-material/Add'
import { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { DeckMappDialog } from 'components/DeckMappDialog'
import { useSnackbar } from 'notistack'
import { DeckRevisions, RevisionComments } from 'models'
import { useCurrentUser } from 'hooks'
import { updateRevision } from 'data/revisionOperations'
import { DataStore } from 'aws-amplify'
import dayjs from 'dayjs'
import { useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'

export const CommentModal = forwardRef(function DescriptionModal_(_, ref) {
  const [comment, setComment] = useState('')
  useImperativeHandle(ref, () => {
    return {
      comment
    }
  })

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item xs={12}>
        <OutlinedInput
          placeholder="Comment to add"
          color="secondary"
          value={comment}
          onChange={({ target: { value } }) => {
            setComment(value)
          }}
          fullWidth
        />
      </Grid>
    </Grid>
  )
})

export const Comments = ({ deckRevision }) => {
  const [showModal, setShowModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const { setDeckRevision } = useCurrentDeckContext()

  const commentRef = useRef({
    comment: ''
  })

  const { getTransactionMetaData } = useCurrentUser()
  const { enqueueSnackbar } = useSnackbar()
  const onSavePressed = async () => {
    setIsSaving(true)
    const clone = structuredClone(deckRevision)
    const comments = clone.Comments || []

    comments.push(
      new RevisionComments({
        Comment: commentRef.current.comment,
        CreatedBy: getTransactionMetaData()
      })
    )
    clone.Comments = comments
    const original = await DataStore.query(DeckRevisions, deckRevision.id)

    const revision = await updateRevision(original, clone, getTransactionMetaData())
    setDeckRevision(structuredClone(revision))
    enqueueSnackbar('Comment has been added', {
      variant: 'success'
    })

    setIsSaving(false)
    setShowModal(false)
  }

  return (
    <>
      <DeckAccordion
        title="Comments"
        showTooltip={false}
        defaultExpanded={false}
        titleComponent={
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              setShowModal(true)
            }}>
            <AddIcon color="secondary" />
          </IconButton>
        }>
        {deckRevision.Comments && (
          <List
            sx={{
              maxHeight: '300px',
              overflowY: 'scroll'
            }}>
            {deckRevision.Comments.map((comment) => {
              return (
                <>
                  <ListItem
                    sx={{
                      height: '60px'
                    }}
                    key={comment.Comment}>
                    <ListItemText
                      primary={comment.Comment}
                      secondaryTypographyProps={{
                        fontSize: '12px',
                        textAlign: 'right'
                      }}
                      primaryTypographyProps={{
                        fontSize: '15px',
                        marginBottom: '.8em'
                      }}
                      secondary={`${comment.CreatedBy.Name} - ${dayjs(comment.CreatedBy.Date).format(
                        'YYYY-MM-DD HH:MM'
                      )}`}
                    />
                  </ListItem>
                  <Divider variant="middle" component="li" />
                </>
              )
            })}
          </List>
        )}
      </DeckAccordion>

      {showModal && (
        <DeckMappDialog
          open
          isLoading={isSaving}
          saveButtonProps={{
            onClick: () => {
              if (!commentRef.current.comment) {
                enqueueSnackbar('No comment to add', {
                  variant: 'info'
                })

                return
              }

              onSavePressed()
            },
            title: 'Save'
          }}
          title="Comments"
          onCancel={() => setShowModal(false)}>
          <CommentModal ref={commentRef} />
        </DeckMappDialog>
      )}
    </>
  )
}
